import { AGSRecordBuilder } from "ags/integration/builders/records/abstract-ags-record-builder";
import holdAndSpinScatterResultBuilder from "ags/integration/builders/results/hold-and-spin-scatter-result-builder";
import { HoldAndSpinRecord } from "ags/integration/records/hold-and-spin-record";
import { HoldAndSpinSchema, HoldAndSpinSymbolSchema, AGSSpinSchema, AGSPickSchema } from "ags/integration/schema/ags-spin-response-schema";
import { getReelIndexFromCoordinate } from "ags/utils/flat-matrix-conversion";
import Gameplay from "slotworks/model/gameplay/gameplay";
import FreespinWinResult from "slotworks/model/gameplay/records/results/freespin-win-result";
import PickRequestPayload from "slotworks/model/gameplay/requests/pick-request-payload";
import slotDefinition from "slotworks/model/slot-definition";
import mcPickRecordGridGenerator from "utils/mc-pick-record-grid-generator";

export class HoldAndSpinRecordBuilder extends AGSRecordBuilder {

    public id: string = "HoldAndSpinRecordBuilder";

    public filter(request: any, response: any): boolean {
        return request instanceof PickRequestPayload;
    }

    public build(gameplay: Gameplay, request: any, response: any): HoldAndSpinRecord[] {
        super.build(gameplay, request, response);

        const parsedResponse = this.getRootAGSRecord(response);
        const spinData = parsedResponse.publicState.spin;
        const allFeatures = spinData.childFeature;
        const feature = allFeatures[allFeatures.length - 1] as HoldAndSpinSchema;

        return [this.buildRecord(feature, gameplay, spinData)];
    }

    public buildRecord(feature: HoldAndSpinSchema, gameplay: Gameplay, spinData: AGSSpinSchema): HoldAndSpinRecord {
        const record = new HoldAndSpinRecord();
        record.complete = feature.complete;
        record.hasChildren = false;
        record.reelset = slotDefinition.reelsets.get("multireel");
        record.results.push(...holdAndSpinScatterResultBuilder.build(record, feature));
        record.grid = this.getGridSymbolsFromCooridnates(feature.MCSymbols);
        record.freespinsRemaining = feature.freeSpinsLeft;
        record.cashWon = feature.cashWon || 0;
        record.freespinsSpun = feature.freeSpinsspun;
        record.issuesRetrigger = false;

        record.lastChild = this.isLastChild(feature, gameplay, spinData);

        return record;
    }

    /**
     * This code is horrible because the server response is horrible.
     *
     * lastChild needs to be true only when there are no free games following the hold and spin bonus and if the
     * hold and spin bonus marked as complete is the last hold and spin bonus.
     */
    protected isLastChild(latestPick: HoldAndSpinSchema, gameplay: Gameplay, spinData: AGSSpinSchema) {

        const latestPickIsNotComplete = latestPick.complete;

        if (!latestPickIsNotComplete) {
            return false;
        }

        const rootRecord = gameplay.getRootRecord();
        const hasFreespins = rootRecord && rootRecord.getResultsOfType(FreespinWinResult);

        if (hasFreespins) {
            return false;
        }

        const onlyHasOnePickBonus = spinData.wins.filter((win) => win.featureType === "pick").length === 1;
        if (onlyHasOnePickBonus) {
            return latestPick.complete;
        }

        // If there are any picks from the first bonus, we'll know that the latestPick.complete will be for the first of two bonuses.
        const anyFeaturesFromFirstPick = (spinData.childFeature as HoldAndSpinSchema[]).some((feature) => feature.BonusNumber === 0);
        if (anyFeaturesFromFirstPick) {
            return false;
        }

        return latestPick.complete;
    }

    protected getGridSymbolsFromCooridnates(symbols: HoldAndSpinSymbolSchema[]): string[][] {
        const grid: string[][] = new Array();
        for (const symbol of symbols) {
            const index = getReelIndexFromCoordinate(symbol);
            const symbolName = this.getSymbolNameFromId(symbol.symbol);
            grid[index] = [symbolName];
        }
        return grid;
    }

    protected getSymbolNameFromId(id: number): string {
        return slotDefinition.symbolDefinitions[id].id;
    }
}

export default new HoldAndSpinRecordBuilder();
